import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Loader2, Lock, SearchX } from 'lucide-react';
import api from '@/lib/axios';
import { formatCurrency } from '@/lib/utils';
import type { TareaRelacion } from '@/types/tareas';

/*
 * Resúmenes de solo lectura de las entidades relacionadas con una tarea. Cada uno usa el endpoint
 * de detalle existente y por tanto hereda sus permisos: si el usuario no tiene acceso, la pestaña
 * lo dice y el resto de la tarea sigue funcionando. No se muestran notas clínicas.
 */

const QUERY_STALE_MS = 60_000;
const PROXIMAS_CITAS = 3;

const fechaHora = (v?: string | Date | null) =>
  v ? new Date(v).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fecha = (v?: string | Date | null) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const legible = (v?: string | null) => (v ? v.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '—');

function edad(nacimiento?: string | null): string {
  if (!nacimiento) return '—';
  const n = new Date(nacimiento);
  const hoy = new Date();
  let anios = hoy.getFullYear() - n.getFullYear();
  if (hoy < new Date(hoy.getFullYear(), n.getMonth(), n.getDate())) anios -= 1;
  return `${anios} años`;
}

const nombreDe = (p?: { nombre?: string; apellidoPaterno?: string; apellidoMaterno?: string } | null) =>
  p ? [p.nombre, p.apellidoPaterno, p.apellidoMaterno].filter(Boolean).join(' ') : '—';

function useDetalle<T>(clave: string, id: string, url: string) {
  return useQuery<T>({
    queryKey: ['tareas', 'rel', clave, id],
    queryFn: () => api.get(url).then(r => r.data),
    staleTime: QUERY_STALE_MS,
    retry: false,
  });
}

/* ── Piezas ──────────────────────────────────────────────────────────────── */

function Dato({ etiqueta, valor }: { etiqueta: string; valor: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">{etiqueta}</dt>
      <dd className="text-sm text-text-primary break-words">{valor || '—'}</dd>
    </div>
  );
}

function Cuadricula({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</dl>;
}

function Estado({ cargando, error }: { cargando: boolean; error: unknown }) {
  if (cargando) {
    return <div className="py-12 flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>;
  }
  const status = (error as any)?.response?.status;
  const sinAcceso = status === 403;
  const Icono = sinAcceso ? Lock : SearchX;
  return (
    <div className="py-12 text-center">
      <Icono className="mx-auto text-gray-300" size={34} />
      <p className="mt-3 text-sm font-medium text-text-primary">
        {sinAcceso ? 'No tienes acceso a esta información' : 'No se encontró la información'}
      </p>
      <p className="text-sm text-text-secondary">
        {sinAcceso ? 'Tu rol no permite ver este registro. El resto de la tarea no se ve afectado.' : 'Es posible que haya sido eliminada.'}
      </p>
    </div>
  );
}

function Encabezado({ titulo, subtitulo, ruta, etiquetaRuta }: { titulo: string; subtitulo?: string; ruta: string; etiquetaRuta: string }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-text-primary break-words">{titulo}</h3>
        {subtitulo && <p className="text-xs text-text-secondary">{subtitulo}</p>}
      </div>
      <Link to={ruta} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-primary-700 hover:bg-primary-50 whitespace-nowrap flex-shrink-0">
        <ExternalLink size={14} /> {etiquetaRuta}
      </Link>
    </div>
  );
}

/* ── Paciente ────────────────────────────────────────────────────────────── */

function PacienteResumen({ id }: { id: string }) {
  const { data: p, isLoading, error } = useDetalle<any>('paciente', id, `/api/pacientes/${id}`);
  const { data: citas = [] } = useQuery<any[]>({
    queryKey: ['tareas', 'rel', 'paciente-citas', id],
    queryFn: () => api.get('/api/citas', { params: { pacienteId: id, fechaInicio: new Date().toISOString() } }).then(r => r.data),
    enabled: !!p,
    staleTime: QUERY_STALE_MS,
    retry: false,
  });

  if (isLoading || error || !p) return <Estado cargando={isLoading} error={error} />;

  const proximas = [...citas]
    .filter(c => c.estado !== 'cancelada' && c.estado !== 'no_asistio')
    .sort((a, b) => +new Date(a.fechaInicio) - +new Date(b.fechaInicio))
    .slice(0, PROXIMAS_CITAS);

  return (
    <>
      <Encabezado titulo={nombreDe(p)} subtitulo="Paciente" ruta={`/pacientes/${id}`} etiquetaRuta="Abrir ficha completa" />
      <Cuadricula>
        <Dato etiqueta="Edad" valor={edad(p.fechaNacimiento)} />
        <Dato etiqueta="Género" valor={legible(p.genero)} />
        <Dato etiqueta="Teléfono" valor={p.telefono} />
        <Dato etiqueta="Correo" valor={p.email} />
        <Dato etiqueta="Tipo de sangre" valor={p.tipoSangre && p.tipoSangre !== 'desconocido' ? p.tipoSangre : '—'} />
        <Dato etiqueta="Médico titular" valor={p.medicoTitular?.nombre} />
        <div className="sm:col-span-2"><Dato etiqueta="Alergias" valor={p.alergias} /></div>
      </Cuadricula>

      <h4 className="mt-6 mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Próximas citas</h4>
      {proximas.length === 0
        ? <p className="text-sm text-text-secondary">Sin citas próximas.</p>
        : (
          <ul className="space-y-1.5">
            {proximas.map(c => (
              <li key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                <span className="text-text-primary">{fechaHora(c.fechaInicio)}</span>
                <span className="text-text-secondary truncate">{c.motivo || legible(c.tipo)} · {legible(c.estado)}</span>
              </li>
            ))}
          </ul>
        )}
    </>
  );
}

/* ── Consulta ────────────────────────────────────────────────────────────── */

function ConsultaResumen({ id }: { id: string }) {
  const { data: c, isLoading, error } = useDetalle<any>('consulta', id, `/api/consultas/${id}`);
  if (isLoading || error || !c) return <Estado cargando={isLoading} error={error} />;

  return (
    <>
      <Encabezado titulo={`Consulta · ${fecha(c.inicioEn ?? c.createdAt)}`} subtitulo={nombreDe(c.paciente)} ruta={c.citaId ? `/consultas/${c.citaId}` : `/pacientes/${c.pacienteId}`} etiquetaRuta={c.citaId ? 'Abrir consulta' : 'Abrir ficha del paciente'} />
      <Cuadricula>
        <Dato etiqueta="Estado" valor={legible(c.estado)} />
        <Dato etiqueta="Tipo" valor={legible(c.tipo)} />
        <Dato etiqueta="Inicio" valor={fechaHora(c.inicioEn)} />
        <Dato etiqueta="Fin" valor={fechaHora(c.finEn)} />
        <Dato etiqueta="Médico" valor={c.medico?.nombre} />
        <Dato etiqueta="Cita ligada" valor={c.cita ? fechaHora(c.cita.fechaInicio) : null} />
        <div className="sm:col-span-2">
          <Dato etiqueta="Diagnóstico (CIE-11)" valor={c.tituloCie11 ? `${c.tituloCie11}${c.codigoCie11 ? ` (${c.codigoCie11})` : ''}` : null} />
        </div>
      </Cuadricula>
    </>
  );
}

/* ── Cita ────────────────────────────────────────────────────────────────── */

function CitaResumen({ id }: { id: string }) {
  const { data: c, isLoading, error } = useDetalle<any>('cita', id, `/api/citas/${id}`);
  if (isLoading || error || !c) return <Estado cargando={isLoading} error={error} />;

  return (
    <>
      <Encabezado
        titulo={`Cita · ${fechaHora(c.fechaInicio)}`}
        subtitulo={nombreDe(c.paciente)}
        ruta={`/consultas/${id}`}
        etiquetaRuta="Abrir consulta"
      />
      <Cuadricula>
        <Dato etiqueta="Estado" valor={legible(c.estado)} />
        <Dato etiqueta="Tipo" valor={legible(c.tipo)} />
        <Dato etiqueta="Inicio" valor={fechaHora(c.fechaInicio)} />
        <Dato etiqueta="Fin" valor={fechaHora(c.fechaFin)} />
        <Dato etiqueta="Médico" valor={c.medico?.nombre} />
        <Dato etiqueta="Servicio" valor={c.servicioNombre} />
        <div className="sm:col-span-2"><Dato etiqueta="Motivo" valor={c.motivo} /></div>
        <Dato etiqueta="Cobro" valor={legible(c.estadoCobro)} />
      </Cuadricula>
    </>
  );
}

/* ── Presupuesto ─────────────────────────────────────────────────────────── */

function PresupuestoResumen({ id, pacienteId }: { id: string; pacienteId: string | null }) {
  const { data: p, isLoading, error } = useDetalle<any>('presupuesto', id, `/api/presupuestos/${id}`);
  if (isLoading || error || !p) return <Estado cargando={isLoading} error={error} />;

  return (
    <>
      <Encabezado
        titulo={p.titulo}
        subtitulo={`Presupuesto · ${nombreDe(p.paciente)}`}
        ruta={`/pacientes/${pacienteId ?? p.pacienteId}`}
        etiquetaRuta="Abrir ficha del paciente"
      />
      <Cuadricula>
        <Dato etiqueta="Estado" valor={legible(p.estado)} />
        <Dato etiqueta="Vigencia" valor={fecha(p.vigenciaFin)} />
        <Dato etiqueta="Total" valor={formatCurrency(Number(p.total ?? 0))} />
        <Dato etiqueta="Pagado" valor={formatCurrency(Number(p.totalPagado ?? 0))} />
        <Dato etiqueta="Saldo pendiente" valor={formatCurrency(Number(p.saldoPendiente ?? 0))} />
        <Dato etiqueta="Médico" valor={p.medico?.nombre} />
      </Cuadricula>

      <h4 className="mt-6 mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Partidas</h4>
      {!p.items?.length
        ? <p className="text-sm text-text-secondary">Sin partidas.</p>
        : (
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm min-w-[22rem]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-text-secondary border-b border-border">
                  <th className="px-3 py-2 font-semibold">Concepto</th>
                  <th className="px-3 py-2 font-semibold text-right">Cant.</th>
                  <th className="px-3 py-2 font-semibold text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {p.items.map((i: any) => (
                  <tr key={i.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-text-primary">{i.nombre}</td>
                    <td className="px-3 py-2 text-right text-text-secondary">{Number(i.cantidad)}</td>
                    <td className="px-3 py-2 text-right text-text-primary">{formatCurrency(Number(i.subtotal ?? 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
}

/* ── Selector por tipo ───────────────────────────────────────────────────── */

export function RelacionResumen({ relacion }: { relacion: TareaRelacion }) {
  if (!relacion.entidadId) return <Estado cargando={false} error={null} />;
  switch (relacion.tipo) {
    case 'paciente': return <PacienteResumen id={relacion.entidadId} />;
    case 'consulta': return <ConsultaResumen id={relacion.entidadId} />;
    case 'cita': return <CitaResumen id={relacion.entidadId} />;
    case 'presupuesto': return <PresupuestoResumen id={relacion.entidadId} pacienteId={relacion.pacienteId} />;
    default: return null;
  }
}
